import { useState, useCallback, useMemo } from 'react';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { Pipeline } from './components/Pipeline/Pipeline';
import { Ideas } from './components/Ideas';
import { Recordings } from './components/Recordings';
import { useContentStorage } from './hooks/useContentStorage';
import { generateId, addDays, isOverdue } from './utils/helpers';

export default function App() {
  const [view, setView] = useState('dashboard');
  const [user, setUser] = useState('zein');
  const [ideaInput, setIdeaInput] = useState('');
  const [showIdea, setShowIdea] = useState(false);

  const { content, setContent, recordings, setRecordings, loading } = useContentStorage();

  const add = useCallback((title, urgent) => {
    setContent([
      ...content,
      {
        id: generateId(),
        title,
        status: 'idea',
        platforms: [],
        type: '',
        urgent,
        deadlines: {},
        links: {},
        notes: '',
        cutdowns: [],
        by: user,
        at: Date.now()
      }
    ]);
  }, [content, user, setContent]);

  const move = useCallback((cid, to) => {
    setContent(content.map(c =>
      c.id === cid
        ? {
            ...c,
            status: to,
            deadlines: to === 'editing' && !c.deadlines.edit
              ? { ...c.deadlines, edit: addDays(2) }
              : c.deadlines
          }
        : c
    ));
  }, [content, setContent]);

  const del = useCallback((cid) => {
    setContent(content.filter(c => c.id !== cid));
  }, [content, setContent]);

  const upd = useCallback((cid, data) => {
    setContent(content.map(c => (c.id === cid ? { ...c, ...data } : c)));
  }, [content, setContent]);

  const clearPublished = useCallback(() => {
    setContent(content.filter(c => c.status !== 'published'));
  }, [content, setContent]);

  const stats = useMemo(() => ({
    ideas: content.filter(c => c.status === 'idea').length,
    pending: content.filter(c => c.status === 'pending_approval').length,
    active: content.filter(c =>
      ['approved', 'scripting', 'recording', 'editing', 'thumbnail', 'scheduled'].includes(c.status)
    ).length,
    overdue: content.filter(c =>
      (isOverdue(c.deadlines?.edit) || isOverdue(c.deadlines?.publish)) &&
      c.status !== 'published'
    ).length,
    cutdowns: content.reduce((sum, c) => sum + (c.cutdowns?.length || 0), 0),
  }), [content]);

  const quickAdd = useCallback(() => {
    if (ideaInput.trim()) {
      add(ideaInput.trim(), false);
      setIdeaInput('');
      setShowIdea(false);
    }
  }, [ideaInput, add]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400 text-lg">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <Header
        user={user}
        setUser={setUser}
        ideaInput={ideaInput}
        setIdeaInput={setIdeaInput}
        showIdea={showIdea}
        setShowIdea={setShowIdea}
        onQuickAdd={quickAdd}
      />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar view={view} setView={setView} stats={stats} user={user} />

        <main className="flex-1 overflow-auto p-4 md:p-6 pb-20 md:pb-6">
          {view === 'dashboard' && (
            <Dashboard content={content} user={user} move={move} stats={stats} />
          )}
          {view === 'pipeline' && (
            <Pipeline
              content={content}
              move={move}
              del={del}
              upd={upd}
              user={user}
              clearPublished={clearPublished}
            />
          )}
          {view === 'ideas' && (
            <Ideas content={content} add={add} move={move} del={del} upd={upd} user={user} />
          )}
          {view === 'recordings' && (
            <Recordings
              recordings={recordings}
              setRecordings={setRecordings}
              content={content}
            />
          )}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <Sidebar view={view} setView={setView} stats={stats} user={user} mobile />
    </div>
  );
}
